import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LoadingState } from "@/components/ui/StateBoundaries";
import ItemSearch from "@/components/purchase/ItemSearch";
import * as purchaseApi from "@/lib/purchaseApi";

/**
 * What a vendor supplies, set up ahead (owner, 2026-11-01: "we can presave what all
 * items come from a vendor"). Picking the vendor on a bill lists these at their last
 * rate and quantity 0. Entering a bill adds its items here by itself.
 */
const reason = err => err?.response?.data?.detail || err?.message || "no answer from the server";

export default function VendorItems({ supplier, stockItems, token, onClose }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    purchaseApi.listSupplierItems(supplier.id, token)
      .then(list => setRows(list.map(i => ({ ...i, rate: i.last_unit_cost ?? "" }))))
      .catch(err => setError(reason(err)));
  }, [supplier.id, token]);

  const add = item => setRows(r => [...r, { stock_item_id: item.id, name: item.name, unit: item.unit, rate: "", last_tax_percent: 0 }]);

  const save = async () => {
    for (const r of rows) {
      if (r.rate !== "" && (!Number.isFinite(Number(r.rate)) || Number(r.rate) < 0)) return toast.error(`${r.name}: the rate must be a number, 0 or more`);
    }
    setSaving(true);
    try {
      await purchaseApi.setSupplierItems(supplier.id, rows.map(r => ({
        stock_item_id: r.stock_item_id, ...(r.rate !== "" ? { last_unit_cost: Number(r.rate) } : {}) })), token);
      toast.success(`${supplier.name}: ${rows.length} item${rows.length === 1 ? "" : "s"} saved`);
      onClose();
    } catch (err) {
      toast.error(`Not saved: ${reason(err)}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title={`Items from ${supplier.name}`} onClose={onClose} testId="vendor-items"
           footer={<><Button variant="outline" onClick={onClose}>Cancel</Button>
             <Button variant="action" disabled={saving || !rows} onClick={save} data-testid="vendor-items-save">
               {saving ? "Saving…" : "Save list"}
             </Button></>}>
      <div className="space-y-base">
        <p className="text-meta text-muted-foreground">
          These come up when you enter this vendor's bill, at the last rate with quantity 0. Items you add while
          entering a bill join this list by themselves.
        </p>
        {error && <p className="text-meta text-danger" role="alert">List not loaded: {error}</p>}
        {!error && !rows && <LoadingState message="Reading the vendor's items…" />}
        {rows && (
          <>
            <ItemSearch items={stockItems} exclude={rows.map(r => r.stock_item_id)} onPick={add} testId="vendor-item-search" />
            {rows.length === 0 && <p className="text-meta text-muted-foreground">No items yet.</p>}
            {rows.map((r, i) => (
              <div key={r.stock_item_id} className="flex items-center gap-snug" data-testid={`vendor-item-${r.stock_item_id}`}>
                <span className="flex-1 min-w-0 truncate text-body">{r.name} <span className="text-micro text-muted-foreground">{r.unit}</span></span>
                <Input type="number" min="0" step="0.01" placeholder="Rate" className="w-24 h-11 text-right tabular-nums"
                       value={r.rate} aria-label={`Rate for ${r.name}`}
                       onChange={e => setRows(list => list.map((x, j) => (j === i ? { ...x, rate: e.target.value } : x)))}
                       data-testid={`vendor-item-${r.stock_item_id}-rate`} />
                <Button variant="ghost" size="icon" aria-label={`Remove ${r.name}`}
                        onClick={() => setRows(list => list.filter((_, j) => j !== i))}
                        data-testid={`vendor-item-${r.stock_item_id}-remove`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            ))}
          </>
        )}
      </div>
    </Modal>
  );
}
